import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress,
  Alert
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import configService from '../../services/configService';

const Auth0Login = () => {
  const { 
    isAuthenticated, 
    isLoading, 
    error, 
    loginWithRedirect 
  } = useAuth0();
  
  const location = useLocation();
  const [config, setConfig] = useState(null);

  // Load configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const appConfig = await configService.getConfig();
        setConfig(appConfig);
      } catch (error) {
        console.error('Error loading config in Auth0Login:', error);
      }
    };
    loadConfig();
  }, []);

  // Note: No navigation logic here - App component handles routing based on auth state

  const handleLogin = async () => {
    try {
      if (!config) {
        console.error('Configuration not loaded yet');
        return;
      }

      // Clear any stale Auth0 data before login
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            localStorage.removeItem(key);
          }
        });

        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            sessionStorage.removeItem(key);
          }
        });

        console.log('✅ Cleared stale Auth0 data before login');
      } catch (e) {
        console.warn('Could not clear stale data:', e);
      }

      console.log('🔄 Starting Auth0 login with config:', {
        audience: config.auth0.audience,
        domain: config.auth0.domain,
        scope: config.auth0.scope
      });

      await loginWithRedirect({
        authorizationParams: {
          screen_hint: 'login',
          audience: config.auth0.audience,
          scope: config.auth0.scope || 'openid profile email read:assigned-projects create:wps update:timesheet offline_access'
        },
        appState: {
          returnTo: location.state?.from?.pathname || '/'
        }
      });
    } catch (err) {
      console.error('❌ Login error:', err);
    }
  };

  const handleClearCache = () => {
    try {
      console.log('🧹 Clearing Auth0 cache...');

      // Clear all Auth0 related localStorage items
      Object.keys(localStorage).forEach(key => {
        if (key.includes('@@auth0spajs@@')) {
          console.log('🗑️ Removing:', key);
          localStorage.removeItem(key);
        }
      });

      console.log('✅ Auth0 cache cleared, reloading page...');
      window.location.reload();
    } catch (error) {
      console.error('❌ Error clearing cache:', error);
    }
  };

  // If already authenticated, this component shouldn't render (App handles routing)
  if (isAuthenticated) {
    console.log('✅ User is authenticated, App component will handle routing');
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
              Redirecting...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
              Loading...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: '100%',
            maxWidth: 400,
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              p: 3,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom>
              Time Management
            </Typography>
            <Typography variant="subtitle1">
              Please sign in to continue
            </Typography>
          </Box>

          {/* Login Content */}
          <Box sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error.message || 'Authentication error occurred'}
              </Alert>
            )}

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Sign in with your company credentials to access the Time Management System.
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={!config}
              startIcon={<LoginIcon />}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                }
              }}
            >
              {!config ? 'Loading...' : 'Sign In'}
            </Button>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Secure authentication powered by Auth0
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Auth0Login;
